import { Product, ProductVariant } from 'types/Product';
import { FriendlySiteName, SiteName } from 'types/Site';
import BrandTypes from 'constants/brand';
import { StockCode } from 'types/ProductResponseType';
import { GetImageUrl } from './media/media-util';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
(global as any).REDIS_INSTANCE_IS_HEALTHY = true;

export const atgOrigin = (window: Window) =>
  window?.location?.origin.indexOf('local') > -1 ? 'http://127.0.0.1:3002' : '';

export const IsObjectEmpty = (empty: object) => Object.keys(empty).length === 0 && empty.constructor === Object;

export function isOnlyDigitInString(string: string) {
  return /(^\d+$)/.test(string);
}

export function stringContainsOnlyAlphanumericCharacters(string: string) {
  return /^[A-Za-z0-9]*$/.test(string);
}

export function getArticle(string: string) {
  if (!/^[a-zA-Z]/.test(string)) {
    throw new Error('First character is not valid');
  }
  if (/^[a,e,u,i,o]/.test(string.toLowerCase())) {
    return `an ${string}`;
  }
  return `a ${string}`;
}

export function transformStockPrimaryForSchema(stockStatus: string | undefined): string | undefined {
  switch (stockStatus) {
    case 'Low stock':
      return 'LimitedAvailability';
    case 'Out of stock':
      return 'OutOfStock';
    case 'Awaiting stock':
      return 'LimitedAvailability';
    case 'In stock':
      return 'InStock';
    case 'Pre-order now':
      return 'PreOrder';
    default:
      return stockStatus;
  }
}

export function mapStockCodeToSchemaAvailability(stockCode: StockCode | undefined): string | undefined {
  if (!stockCode) {
    return undefined;
  }

  switch (stockCode) {
    case StockCode.OUTOFSTOCK:
    case StockCode.UNAVAILABLE:
      return 'https://schema.org/OutOfStock';
    case StockCode.DCSTOCK:
    case StockCode.DDSTOCKWORKINGDAYS:
    case StockCode.DDSTOCKWEEK:
    case StockCode.DDSTOCKDATE:
    case StockCode.DDSTOCKNEXTDATE:
    case StockCode.DDSTOCK:
      return 'https://schema.org/InStock';
    case StockCode.LOWSTOCK:
      return 'https://schema.org/LimitedAvailability';
    case StockCode.PREORDER:
    case StockCode.HOLDDAY:
    case StockCode.HOLDWEEKS:
    case StockCode.HOLDDATE:
      return 'https://schema.org/PreOrder';
    default:
      return undefined;
  }
}

export const getFriendlySiteName = (site: SiteName): FriendlySiteName => {
  switch (site) {
    case BrandTypes.VERYIE:
      return 'Very Ireland';
    case BrandTypes.LITTLEWOODS:
      return 'Littlewoods';
    default:
      return 'Very';
  }
};

/**
 * Extracts the number of return days from the return policy text
 * @param beforeLink - The return policy text
 * @returns The number of days, or undefined if not found
 */
const extractReturnDays = (beforeLink: string): number | undefined => {
  const match = beforeLink.match(/(\d+)\s*day/i);
  return match ? parseInt(match[1], 10) : undefined;
};

/**
 * Determines if a product is non-returnable based on the policy text
 * @param beforeLink - The return policy text
 * @returns true if the product is non-returnable
 */
const isNonReturnable = (beforeLink: string): boolean => {
  const lowerText = beforeLink.toLowerCase();
  // If it explicitly says "we will replace or refund", it's returnable
  if (lowerText.includes('we will replace or refund') || lowerText.includes('replace or refund')) {
    return false;
  }
  // If it says "non-returnable", it's non-returnable (even if it mentions days)
  return lowerText.includes('non-returnable') || lowerText.includes('non returnable');
};

/**
 * Gets the country code for a given site
 * @param site - The site name
 * @returns The ISO country code (GB or IE)
 */
const getCountryCode = (site: SiteName): string => {
  switch (site) {
    case 'veryie':
      return 'IE';
    case 'very':
    case 'littlewoods':
    default:
      return 'GB';
  }
};

/**
 * Converts a relative URL to an absolute URL using the origin from canonicalUrl
 * @param relativeUrl - The relative URL path
 * @param canonicalUrl - The canonical URL to extract origin from
 * @returns The absolute URL
 */
const makeAbsoluteUrl = (relativeUrl: string, canonicalUrl: string): string => {
  // If already absolute, return as is
  if (relativeUrl.startsWith('http://') || relativeUrl.startsWith('https://')) {
    return relativeUrl;
  }

  // Extract origin from canonicalUrl and append relativeUrl
  const url = new URL(canonicalUrl);
  return `${url.origin}${relativeUrl}`;
};

export const getOffersSEOSchema = (skus: ProductVariant[], site: SiteName) => {
  const sellerName = getFriendlySiteName(site);
  return skus.map((sku) => ({
    '@type': 'Offer',
    sku: sku.option,
    price: sku.price?.amount.current && sku.price.amount.current.replace(/^[£€]/, ''),
    priceCurrency: sku.price?.amount.currencyCode && sku.price.amount.currencyCode,
    availability: transformStockPrimaryForSchema(sku.stock?.primary),
    itemCondition: 'http://schema.org/NewCondition',
    seller: {
      '@type': 'Organization',
      name: sellerName,
    },
  }));
};

export const getSchemaLayerSEO = (product: Product, site: SiteName) => {
  const { skus, description, name, images, reviews, brand } = product;

  const stars = reviews?.stars ?? 0;
  const total = reviews?.total ?? 0;
  const cleanName = name?.replace(/"/g, '') ?? '';
  const cleanDescription = description?.replace(/"/g, '') ?? '';

  const productSchema: any = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    offers: getOffersSEOSchema(skus, site),
    name: cleanName,
    image: GetImageUrl({
      identifier: images?.[0]?.identifier,
      template: '$pdp_48x64_x2$',
      format: 'jpg',
      site,
    }),
    description: cleanDescription,
  };

  if (brand && brand.trim().length > 0) {
    productSchema.brand = {
      '@type': 'Brand',
      name: brand.trim().toUpperCase(),
    };
  }

  if (total >= 1) {
    productSchema.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: String(stars),
      reviewCount: String(total),
      bestRating: '5',
      worstRating: '1',
    };
  }

  return `
    <script type="application/ld+json">
      ${JSON.stringify(productSchema, null, 2)}
    </script>
  `;
};

export const buildProductJsonLd = (product: Product, site: SiteName, canonicalUrl: string): Record<string, unknown> => {
  const { skus, description, name, images, reviews, brand, identification, returnable } = product;

  const cleanName = name?.replace(/"/g, '') ?? '';
  const cleanDescription = description?.replace(/"/g, '') ?? '';

  // Build image array
  const imageUrls =
    images
      ?.map((img) =>
        GetImageUrl({
          identifier: img.identifier,
          template: '$pdp_48x64_x2$',
          format: 'jpg',
          site,
        }),
      )
      .filter((url): url is string => Boolean(url)) ?? [];

  // Check if all variants share the same EAN
  const allEans = skus.map((sku) => sku.ean).filter((ean): ean is string => Boolean(ean));
  const uniqueEans = new Set(allEans);
  const sharedEan = uniqueEans.size === 1 && allEans.length > 0 ? allEans[0] : undefined;

  // Build product schema
  const productSchema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'ProductGroup',
    '@id': `${canonicalUrl}#productgroup`,
    name: cleanName,
    description: cleanDescription,
    url: canonicalUrl,
    productGroupID: identification.productId,
  };

  // Add images if available
  if (imageUrls.length > 0) {
    productSchema.image = imageUrls.length === 1 ? imageUrls[0] : imageUrls;
  }

  // Add brand if available
  if (brand && brand.trim().length > 0) {
    productSchema.brand = {
      '@type': 'Brand',
      name: brand.trim(),
    };
  }

  // Add gtin on parent if all variants share same EAN
  if (sharedEan) {
    productSchema.gtin = sharedEan;
  }

  // Add aggregateRating if reviews exist
  const total = reviews?.total ?? 0;
  const stars = reviews?.stars;
  if (total > 0 && stars !== undefined) {
    productSchema.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: String(stars),
      reviewCount: String(total),
      bestRating: '5',
      worstRating: '1',
    };
  }

  // Compute variesBy array (only standard schema.org dimensions)
  // Single pass through SKUs for efficiency (important for SSR/SEO)
  // Only include dimensions that have 2+ unique values (they actually "vary")
  const uniqueColors = new Set<string>();
  const uniqueSizes = new Set<string>();

  skus.some((sku) => {
    if (sku.options?.colour) {
      uniqueColors.add(sku.options.colour);
    }
    if (sku.options?.size) {
      uniqueSizes.add(sku.options.size);
    }
    // Early exit: if we already have 2+ colors and sizes, no need to continue
    return uniqueColors.size >= 2 && uniqueSizes.size >= 2;
  });

  const variesBy: string[] = [];
  if (uniqueColors.size > 1) {
    variesBy.push('color');
  }
  if (uniqueSizes.size > 1) {
    variesBy.push('size');
  }
  if (variesBy.length > 0) {
    productSchema.variesBy = variesBy;
  }

  // Build hasVariant array
  const variants = skus.map((sku) => {
    const variant: Record<string, unknown> = {
      '@type': 'Product',
    };

    // Build variant-specific name (product name + color + size)
    const variantNameParts = [cleanName];
    if (sku.options?.colour) {
      variantNameParts.push(sku.options.colour);
    }
    if (sku.options?.size) {
      variantNameParts.push(sku.options.size);
    }
    variant.name = variantNameParts.join(' - ');

    // Add variant-specific images
    if (sku.images && sku.images.length > 0) {
      const variantImageUrls = sku.images
        .map((img) =>
          GetImageUrl({
            identifier: img.identifier,
            template: '$pdp_48x64_x2$',
            format: 'jpg',
            site,
          }),
        )
        .filter((url): url is string => Boolean(url));

      if (variantImageUrls.length > 0) {
        variant.image = variantImageUrls.length === 1 ? variantImageUrls[0] : variantImageUrls;
      }
    } else if (imageUrls.length > 0) {
      // Fallback to product images if variant has no specific images
      variant.image = imageUrls.length === 1 ? imageUrls[0] : imageUrls;
    }

    // Add sku if available
    if (sku.option) {
      variant.sku = sku.option;
    }

    // Add url to variant
    variant.url = canonicalUrl;

    // Add gtin if not shared on parent
    if (!sharedEan && sku.ean) {
      variant.gtin = sku.ean;
    }

    // Add mpn if available
    if (sku.manufacturerPartNumber) {
      variant.mpn = sku.manufacturerPartNumber;
    }

    // Add color if available
    if (sku.options?.colour) {
      variant.color = sku.options.colour;
    }

    // Add size if available
    if (sku.options?.size) {
      variant.size = sku.options.size;
    }

    // Add isVariantOf reference
    variant.isVariantOf = {
      '@id': `${canonicalUrl}#productgroup`,
    };

    // Build offer
    const offer: Record<string, unknown> = {
      '@type': 'Offer',
      url: canonicalUrl,
      itemCondition: 'https://schema.org/NewCondition',
    };

    // Add price if available
    const price = sku.price?.amount?.decimal ?? sku.price?.amount?.unformattedPrice;
    if (price !== undefined) {
      offer.price = price;
    }

    // Add priceCurrency if available
    if (sku.price?.amount?.currencyCode) {
      offer.priceCurrency = sku.price.amount.currencyCode;
    }

    // Add availability if stock code is available
    const availability = mapStockCodeToSchemaAvailability(sku.stock?.code);
    if (availability) {
      offer.availability = availability;
    }

    // Add priceValidUntil if available
    if (sku.price?.validUntil) {
      offer.priceValidUntil = sku.price.validUntil;
    }

    // Add seller
    offer.seller = {
      '@type': 'Organization',
      name: getFriendlySiteName(site),
    };

    // Add return policy if available
    if (returnable?.link?.href) {
      const returnDays = extractReturnDays(returnable.beforeLink);
      const isNonReturn = isNonReturnable(returnable.beforeLink);
      const absoluteReturnUrl = makeAbsoluteUrl(returnable.link.href, canonicalUrl);

      if (isNonReturn) {
        // For non-returnable items
        offer.hasMerchantReturnPolicy = {
          '@type': 'MerchantReturnPolicy',
          returnPolicyCategory: 'https://schema.org/MerchantReturnNotPermitted',
          applicableCountry: getCountryCode(site),
          merchantReturnLink: absoluteReturnUrl,
        };
      } else if (returnDays) {
        // For returnable items with specific days
        offer.hasMerchantReturnPolicy = {
          '@type': 'MerchantReturnPolicy',
          returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
          merchantReturnDays: returnDays,
          applicableCountry: getCountryCode(site),
          returnMethod: 'https://schema.org/ReturnByMail',
          returnFees: 'https://schema.org/ReturnFeesMerchantResponsibility', // Based on "free returns" in content
          merchantReturnLink: absoluteReturnUrl,
        };
      } else {
        // Fallback: default policy if we can't determine details
        offer.hasMerchantReturnPolicy = {
          '@type': 'MerchantReturnPolicy',
          returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
          merchantReturnDays: 28, // Default based on standard policy
          applicableCountry: getCountryCode(site),
          returnMethod: 'https://schema.org/ReturnByMail',
          returnFees: 'https://schema.org/ReturnFeesMerchantResponsibility',
          merchantReturnLink: absoluteReturnUrl,
        };
      }
    }

    variant.offers = offer;
    return variant;
  });

  productSchema.hasVariant = variants;

  return productSchema;
};

export const isLocal =
  process.env.ENVIRONMENT === 'local' || process.env.ENVIRONMENT === 'ci' || process.env.ENVIRONMENT === 'docker';

export const evaluateCachingStatus = () => {
  const cacheEnabled =
    process.env.ENVIRONMENT !== 'ci' &&
    process.env.REDIS_CACHE_ENABLED === 'true' &&
    (global as any).REDIS_INSTANCE_IS_HEALTHY === true;
  return cacheEnabled;
};

export const isNotInStock = (stockCode?: string): boolean => stockCode === 'OUTOFSTOCK' || stockCode === 'UNAVAILABLE';

export const getProductTitle = ({ brand = '', name = '' }): string => `${brand} ${name}`.trim();

export const getTargettedMovsParam = (): boolean => {
  const queryParams = new URLSearchParams(window.location.search);

  const targettedMovsParam = queryParams.get('targettedMovs');
  const isTargettedMovs = targettedMovsParam === 'true';

  return isTargettedMovs;
};

export const getValidSiteValue = (site?: string): SiteName => {
  const lowerCaseSite = site?.toLowerCase();

  if (lowerCaseSite === 'veryireland' || lowerCaseSite === 'veryie') {
    return 'veryie';
  }

  if (lowerCaseSite === 'littlewoods') {
    return 'littlewoods';
  }

  return 'very';
};

export const getElasticServiceName = (site: SiteName): string => {
  switch (site) {
    case BrandTypes.VERYIE:
      return 'VeryIE - PDP';
    case BrandTypes.LITTLEWOODS:
      return 'Littlewoods - PDP';
    default:
      return 'Very - PDP';
  }
};

export const getBazaarVoiceUrl = (site: SiteName, environment: string) => {
  switch (site) {
    case BrandTypes.VERYIE:
      return `https://apps.bazaarvoice.com/deployments/littlewoods-ie/main_site/${environment}/en_IE/bv.js`;
    case BrandTypes.LITTLEWOODS:
      return `https://apps.bazaarvoice.com/deployments/littlewoods-gb/main_site/${environment}/en_GB/bv.js`;
    default:
      return `https://apps.bazaarvoice.com/deployments/very-gb/main_site/${environment}/en_GB/bv.js`;
  }
};

export const replaceTitleTagValue = (html: string, newValue = '') =>
  html.replace(/<title>(.*?)<\/title>/, `<title>${newValue}</title>`);

export const getSocialMetaTagsStock = (skus: ProductVariant[]): string => {
  const inStockMessage = 'In stock';
  const awaitingStockMessage = 'Awaiting stock';
  const preOrderMessage = 'Pre-order now';
  const outOfStockMessage = 'Out of stock';
  const inStockStatuses = ['In stock', 'Low stock'];
  const statuses = skus.map((sku) => sku.stock?.primary);
  const validStocks = statuses.filter((stock): stock is string => stock !== undefined);

  if (!validStocks.length) return '';

  const atLeastOneSkuInStock = validStocks.some((el) => inStockStatuses.includes(el));
  const atLeastOneSkuAwaitingStock = validStocks.some((el) => el === awaitingStockMessage);
  const atLeastOneSkuPreOrder = validStocks.some((el) => el === preOrderMessage);

  if (atLeastOneSkuInStock) return inStockMessage;
  if (!atLeastOneSkuInStock && atLeastOneSkuAwaitingStock) return awaitingStockMessage;
  if (!atLeastOneSkuInStock && !atLeastOneSkuAwaitingStock && atLeastOneSkuPreOrder) return preOrderMessage;

  return outOfStockMessage;
};

export const captureClientSideError = (error: Error) => {
  if (error instanceof Error && window?.elasticApm?.captureError) {
    window.elasticApm.captureError(error);
  }
};

export const formatError = (error: unknown, defaultCode = 'UNKNOWN_ERROR'): Error => {
  const errorObj = error instanceof Error ? error : new Error(String(error || 'Unknown error'));

  if (
    !Object.prototype.hasOwnProperty.call(errorObj, 'code') ||
    (errorObj as any).code === undefined ||
    (errorObj as any).code === null
  ) {
    (errorObj as any).code = defaultCode;
  }

  return errorObj;
};

export const addApmLabels = (labels: Record<string, string | number | undefined>) => {
  if (typeof window !== 'undefined' && window.elasticApm?.addLabels) {
    try {
      const filteredLabels = Object.fromEntries(Object.entries(labels).filter(([, v]) => v !== undefined)) as Record<
        string,
        string | number | boolean
      >;

      window.elasticApm.addLabels(filteredLabels);
    } catch (err) {
      console.warn('Elastic APM label add failed:', err);
    }
  }
};
